import { useContext, useEffect, useState, useCallback } from 'react';
import { Button } from '@material-ui/core';
import { Add, MoreVert, ExpandMore, ExpandLess } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import EditIcon from '@material-ui/icons/Edit';
import ModalContext from '../../store/ModalContext';
import AuthContext from '../../store/AuthContext';
import addLessonIcon from "../../assets/img/add-lesson-icon.svg"
import deleteLessonIcon from "../../assets/img/Course/delete-lesson.svg"

const truncateCourseTitle = (title, maxLen = 40) => {
    if (!title) return '';
    const dupMatch = title.match(/^(.+?)\s*(\(\d+\))$/);
    if (dupMatch) {
        const base = dupMatch[1];
        const suffix = ` ${dupMatch[2]}`;
        const allowedBase = maxLen - suffix.length;
        const truncatedBase = base.length > allowedBase ? base.slice(0, allowedBase).trimEnd() + '...' : base;
        return truncatedBase + suffix;
    }
    return title.length > maxLen ? title.slice(0, maxLen).trimEnd() + '...' : title;
};

const ManageModulesAndLessons = ({ course, assessmentId, expandedModuleId, onBack, onViewChange, onAddLesson, onEditLesson }) => {

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeModuleId, setActiveModuleId] = useState(null);
    const modalCtx = useContext(ModalContext);
    const authCtx = useContext(AuthContext);
    const [draggedLessonIndex, setDraggedLessonIndex] = useState(null);
    const [draggedModuleId, setDraggedModuleId] = useState(null);
    const [savingLessonId, setSavingLessonId] = useState(null);


    const fetchModules = useCallback(async () => {
        if (!assessmentId || !course?.id) return;
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${course.id}/modules`,
                {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authCtx.token,
                    },
                }
            );
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setModules(prevModules => {
                return (data.data?.modules || []).map(m => {
                    const existing = prevModules.find(prev => prev.id === m.id);
                    const isExpanded = existing ? existing.expanded : (m.id === expandedModuleId);
                    const hasLessons = m.lessons && m.lessons.length > 0;
                    return { ...m, expanded: hasLessons ? (isExpanded || false) : false };
                });
            });
        } catch (err) {
            console.error('Failed to fetch modules:', err);
        } finally {
            setLoading(false);
        }
    }, [assessmentId, course?.id, authCtx.token, expandedModuleId]);

    useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    const toggleExpand = (id) => {
        setModules(prev => prev.map(m => {
            if (m.id === id) {
                const hasLessons = m.lessons && m.lessons.length > 0;
                if (!hasLessons) return m;
                return { ...m, expanded: !m.expanded };
            }
            return m;
        }));
    };


    const handleMenuOpen = (e, id) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setActiveModuleId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveModuleId(null);
    };


    const duplicateModule = useCallback(async (moduleId) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${course.id}/modules/${moduleId}/duplicate`,
                {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authCtx.token,
                    },
                }
            );
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            fetchModules();
        } catch (err) {
            console.error('Failed to duplicate module:', err);
        }
    }, [assessmentId, course?.id, authCtx.token, fetchModules]);

    const deleteLesson = useCallback((module, lesson) => {
        modalCtx.setDetails("delete-lesson-confirmation", {
            deleteUrl: `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${course?.id}/modules/${module.id}/lessons/${lesson.id}`,
            refreshCallback: fetchModules,
            itemName: lesson?.title,
            itemType: 'lesson',
        });
        modalCtx.openModal();
    }, [assessmentId, course?.id, fetchModules, modalCtx]);

    const handleDragStart = (e, moduleId, index) => {
        e.dataTransfer.effectAllowed = "move";
        setTimeout(() => {
            setDraggedLessonIndex(index);
            setDraggedModuleId(moduleId);
        }, 50);
    };

    const handleDragOver = (e, moduleId, index) => {
        e.preventDefault();
        if (draggedModuleId !== moduleId || draggedLessonIndex === null || draggedLessonIndex === index) {
            return;
        }

        setModules(prevModules => {
            return prevModules.map(m => {
                if (m.id === moduleId) {
                    const lessons = [...(m.lessons || [])];
                    const draggedLesson = lessons[draggedLessonIndex];
                    lessons.splice(draggedLessonIndex, 1);
                    lessons.splice(index, 0, draggedLesson);
                    return { ...m, lessons };
                }
                return m;
            });
        });
        setDraggedLessonIndex(index);
    };

    const handleDragEnd = async () => {
        if (draggedModuleId === null || draggedLessonIndex === null) return;
        
        const moduleId = draggedModuleId;
        const module = modules.find(m => m.id === moduleId);
        if (module && module.lessons) {
            const draggedLesson = module.lessons[draggedLessonIndex];
            const draggedLessonId = draggedLesson?.id;
            const lessonIds = module.lessons.map(l => l.id);

            if (draggedLessonId) {
                setSavingLessonId(draggedLessonId);
            }
            setDraggedLessonIndex(null);
            setDraggedModuleId(null);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${course?.id}/modules/${moduleId}/lessons/reorder`,
                    {
                        method: 'PUT',
                        mode: 'cors',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + authCtx.token,
                        },
                        body: JSON.stringify({ lessonIds }),
                    }
                );
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            } catch (err) {
                console.error('Failed to save lesson order:', err);
                fetchModules();
            } finally {
                setSavingLessonId(null);
            }
        } else {
            setDraggedLessonIndex(null);
            setDraggedModuleId(null);
        }
    };

    return (
        <div className='manage-module-main-container'>
            {/* Header */}
            <div className='add-module-cta-container'>
                <div className='manage-module-title'>Course: {course?.name}</div>
                <Button
                    className='add-module-cta'
                    style={{ backgroundColor: '#2C3E50', color: '#fff' }}
                    onClick={() => {
                        modalCtx.setDetails("add-module-modal", {
                            course,
                            assessmentId,
                            refreshModules: fetchModules,
                        });
                        modalCtx.openModal();
                    }}
                >
                    <Add />  NEW MODULE
                </Button>
            </div>

            <div>
                <div className='module-table-header'>
                    <div className='module-th'>
                        <div className='saperator'></div>
                        Module Name
                    </div>
                    <div className='module-th'>
                        <div className='saperator'></div>
                        Actions
                    </div>
                </div>
            </div>

            <div className='module-table'>
                {!loading && modules.length === 0 && (
                    <div className='module-empty'>No modules found. Add your first module!</div>
                )}

                {modules.map((module) => {
                    const hasLessons = module.lessons && module.lessons.length > 0;
                    return (
                        <div key={module.id} className='module-row-wrapper'>

                            {/* Module Row */}
                            <div className={`module-row ${!hasLessons ? 'empty-module' : ''}`} onClick={() => toggleExpand(module.id)}>
                                {(() => {
                                    const title = module.title || '';
                                    const truncatedText = truncateCourseTitle(title);
                                    const isTruncated = title !== truncatedText;

                                    const content = (
                                        <span className='module-name' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {truncatedText}
                                        </span>
                                    );

                                    return isTruncated ? (
                                        <Tooltip title={title} placement="top" arrow>
                                            {content}
                                        </Tooltip>
                                    ) : content;
                                })()}
                                <div className='module-row-actions'>
                                    <div className='add-lesson-cta' onClick={(e) => {
                                        e.stopPropagation();
                                        onAddLesson(module);
                                    }} > <img src={addLessonIcon} alt="addLessonIcon" /> Add Lesson</div>
                                    <IconButton size='small' onClick={(e) => handleMenuOpen(e, module.id)}>
                                        <MoreVert fontSize='small' />
                                    </IconButton>
                                    {hasLessons ?
                                        <IconButton
                                            size='small'
                                            onClick={(e) => { e.stopPropagation(); toggleExpand(module.id); }}
                                            style={{ border: "1px solid #D4D4D4", borderRadius: "100%", width: "20px", height: "20px" }}
                                        >
                                            {module.expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                                        </IconButton> : <div style={{ width: "20px" }}></div>
                                    }
                                </div>
                            </div>

                            {/* Lessons */}
                            {module.expanded && (module.lessons || []).map((lesson, index) => (
                                <div
                                    key={lesson.id}
                                    className={`lesson-row ${draggedLessonIndex === index && draggedModuleId === module.id ? 'dragging' : ''} ${savingLessonId === lesson.id ? 'saving' : ''}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, module.id, index)}
                                    onDragOver={(e) => handleDragOver(e, module.id, index)}
                                    onDragEnd={handleDragEnd}
                                    style={{
                                        cursor: 'grab',
                                        border: draggedLessonIndex === index && draggedModuleId === module.id 
                                            ? '2px dashed #a0a0a0' 
                                            : savingLessonId === lesson.id
                                                ? '2px dashed #a0a0a0'
                                                : undefined,
                                        backgroundColor: draggedLessonIndex === index && draggedModuleId === module.id 
                                            ? '#fafafa' 
                                            : savingLessonId === lesson.id
                                                ? '#f9fafb'
                                                : undefined,
                                        opacity: savingLessonId === lesson.id ? 0.75 : 1,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            visibility: draggedLessonIndex === index && draggedModuleId === module.id ? 'hidden' : 'visible'
                                        }}
                                    >
                                        <div className='lesson-name-status-container'>
                                            <span className='lesson-name'>{lesson.title}</span>
                                            <div className={`${lesson.status === "draft" ? "draft" : "published"}`}>
                                                <span>{lesson.status}</span>
                                            </div>
                                        </div>
                                        <div className='lesson-actions'>
                                            <IconButton size='small' onClick={() => onEditLesson(module, lesson)}>
                                                <EditIcon className="editicon" style={{ color: "#262626" }} />
                                            </IconButton>
                                            <IconButton size='small' onClick={() => deleteLesson(module, lesson)}>
                                                <img src={deleteLessonIcon} alt='deleteLessonIcon' />
                                            </IconButton>
                                        </div>
                                    </div>
                                </div>
                            ))}

                        </div>
                    );
                })}
            </div>

            {/* Module Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ className: 'course-action-menu' }}
            >
                <MenuItem onClick={() => {
                    const module = modules.find(m => m.id === activeModuleId);
                    modalCtx.setDetails("add-module-modal", {
                        course,
                        assessmentId,
                        module,
                        mode: 'edit',
                        refreshModules: fetchModules,
                    });
                    modalCtx.openModal();
                    handleMenuClose();
                }}>Edit Module Details</MenuItem>
                <MenuItem onClick={() => {
                    duplicateModule(activeModuleId);
                    handleMenuClose();
                }}>Duplicate Module</MenuItem>
                <MenuItem onClick={() => {
                    const module = modules.find(m => m.id === activeModuleId);
                    modalCtx.setDetails("delete-module-confirmation", {
                        deleteUrl: `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${course?.id}/modules/${activeModuleId}`,
                        refreshCallback: fetchModules,
                        itemName: module?.title,
                        itemType: 'module',
                    });
                    modalCtx.openModal();
                    handleMenuClose();
                }} style={{ color: '#E05A2B' }}>Delete Module</MenuItem>
            </Menu>
        </div>
    );
};

export default ManageModulesAndLessons;